import { Controller, useFormContext } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { TextField, FormControlLabel, Checkbox } from "@mui/material";
import { CustomSelect } from "../../components/custom-select.component";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { ClusterSelect } from "../../forms/form-input-components/selects/cluster-select/cluster.select";
import { RoundToType } from "../../store/Jobs/utils";
import { applyRounding } from "../../lib/utils/calculate";
import { Separator } from "../../components/ui/separator";

// Keep the MUI label shrunk whenever a value is present, so a programmatically
// computed price/margin can never sit underneath the floating label.
const isFilled = (value: unknown) =>
	value !== undefined && value !== null && value !== "";

interface ArticleFormProps {
	onSubmit: (data: any) => void;
}

export const ArticleForm: React.FC<ArticleFormProps> = ({ onSubmit }) => {
	const { handleSubmit, control, watch, setValue, formState } =
		useFormContext();

	const stateRef = useRef({
		prevValues: {
			purchase_price: undefined,
			margin: undefined,
			price: undefined,
		},
		prevRounding: true,
		isInitial: true,
		isCalculating: false,
	});
	const [useRappenRounding, setUseRappenRounding] = useState(true);

	useEffect(() => {
		const pp = watch("purchase_price");
		const m = watch("margin");
		const p = watch("price");
		const current = { purchase_price: pp, margin: m, price: p };

		if (stateRef.current.isInitial) {
			stateRef.current.prevValues = current;
			stateRef.current.prevRounding = useRappenRounding;
			stateRef.current.isInitial = false;
			return;
		}

		if (stateRef.current.isCalculating) {
			stateRef.current.isCalculating = false;
			stateRef.current.prevValues = current;
			return;
		}

		const [ppNum, mNum, pNum] = [pp, m, p].map((v) =>
			parseFloat(String(v).replace(",", "."))
		);
		const round = (v: number) =>
			useRappenRounding
				? applyRounding(v, RoundToType.TO_RAPPEN)
				: parseFloat(v.toFixed(2));

		const prev = stateRef.current.prevValues;
		const ppChanged = prev.purchase_price !== pp;
		const mChanged = prev.margin !== m;
		const pChanged = prev.price !== p;
		const roundingChanged =
			stateRef.current.prevRounding !== useRappenRounding;
		const changesCount = [ppChanged, mChanged, pChanged].filter(
			Boolean
		).length;

		// Recalculate price when rounding mode changes
		if (roundingChanged && !isNaN(ppNum) && ppNum >= 0 && !isNaN(mNum)) {
			setValue("price", round(ppNum * (1 + mNum / 100)));
			stateRef.current.isCalculating = true;
		} else if (changesCount === 1) {
			let shouldCalculate = false;

			if (ppChanged && !isNaN(ppNum) && ppNum >= 0) {
				if (!isNaN(mNum)) {
					setValue("price", round(ppNum * (1 + mNum / 100)));
					shouldCalculate = true;
				} else if (!isNaN(pNum) && ppNum > 0) {
					setValue("margin", ((pNum / ppNum - 1) * 100).toFixed(2));
					shouldCalculate = true;
				}
			} else if (
				mChanged &&
				!isNaN(mNum) &&
				!isNaN(ppNum) &&
				ppNum >= 0
			) {
				setValue("price", round(ppNum * (1 + mNum / 100)));
				shouldCalculate = true;
			} else if (pChanged && !isNaN(pNum) && !isNaN(ppNum) && ppNum > 0) {
				setValue("margin", ((pNum / ppNum - 1) * 100).toFixed(2));
				shouldCalculate = true;
			}

			if (shouldCalculate) {
				stateRef.current.isCalculating = true;
			}
		}

		stateRef.current.prevValues = current;
		stateRef.current.prevRounding = useRappenRounding;
	}, [
		watch("purchase_price"),
		watch("margin"),
		watch("price"),
		useRappenRounding,
		setValue,
	]);

	const sectionLabel = (label: string) => (
		<div className="flex items-center gap-3 pt-1">
			<span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
				{label}
			</span>
			<Separator className="flex-1" />
		</div>
	);

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="flex flex-col gap-5">
				<div className="flex flex-col gap-3">
					{sectionLabel("Grunddaten")}
					<div className="grid grid-cols-3 gap-3">
						<Controller
							name="code_e"
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<TextField
									label="Artikelcode"
									size="small"
									{...field}
									required
									error={!!formState.errors.code_e}
								/>
							)}
						/>
						<CustomSelect
							inputLabel="Artikelart"
							value={watch("type")?.toString() || ""}
							onChange={(value: string | null) =>
								setValue("type", parseInt(value as string))
							}
							options={[
								{
									value: JobItemTypeEnum.ARTICLE_MATERIAL,
									label: "Material",
								},
								{
									value: JobItemTypeEnum.ARTICLE_POSTAGE,
									label: "Porto",
								},
								{
									value: JobItemTypeEnum.ARTICLE_EXTERNAL_WORK,
									label: "Fremdarbeiten",
								},
								{
									value: JobItemTypeEnum.ARTICLE_DISCOUNTS,
									label: "Abzug/Barrabatt",
								},
								{
									value: JobItemTypeEnum.ARTICLE_FIXED_RATE,
									label: "Arbeitspauschale",
								},
							]}
							size="small"
							required
						/>
						<ClusterSelect
							value={watch("cluster") || ""}
							onChange={(value: string | null) =>
								setValue("cluster", value as string)
							}
							size="small"
							required
						/>
					</div>
					<Controller
						name="description_de"
						control={control}
						render={({ field }) => (
							<TextField
								label="Bezeichnung (DE)"
								size="small"
								{...field}
							/>
						)}
					/>
					<div className="grid grid-cols-2 gap-3">
						<Controller
							name="description_fr"
							control={control}
							render={({ field }) => (
								<TextField
									label="Bezeichnung (FR)"
									size="small"
									{...field}
								/>
							)}
						/>
						<Controller
							name="description_it"
							control={control}
							render={({ field }) => (
								<TextField
									label="Bezeichnung (IT)"
									size="small"
									{...field}
								/>
							)}
						/>
					</div>
				</div>
				<div className="flex flex-col gap-3">
					{sectionLabel("Preis & Verrechnung")}
					<div className="grid grid-cols-3 gap-3">
						<Controller
							name="price"
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<TextField
									type="number"
									label="Preis"
									size="small"
									{...field}
									value={field.value ?? ""}
									required
									error={!!formState.errors.price}
									InputLabelProps={{
										shrink: isFilled(field.value),
									}}
								/>
							)}
						/>
						<Controller
							name="purchase_price"
							control={control}
							render={({ field }) => (
								<TextField
									label="Einstandspreis"
									size="small"
									{...field}
									value={field.value ?? ""}
									type="number"
									InputLabelProps={{
										shrink: isFilled(field.value),
									}}
								/>
							)}
						/>
						<Controller
							name="margin"
							control={control}
							render={({ field }) => (
								<TextField
									label="Marge (%)"
									size="small"
									{...field}
									value={field.value ?? ""}
									InputLabelProps={{
										shrink: isFilled(field.value),
									}}
								/>
							)}
						/>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<Controller
							name="unit"
							control={control}
							render={({ field }) => (
								<TextField
									label="Einheit"
									size="small"
									{...field}
								/>
							)}
						/>
						<Controller
							name="standard_quantity"
							control={control}
							render={({ field }) => (
								<TextField
									label="Vorgabemenge"
									size="small"
									{...field}
								/>
							)}
						/>
					</div>
					<div className="flex flex-wrap gap-x-6">
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={useRappenRounding}
									onChange={(e) =>
										setUseRappenRounding(e.target.checked)
									}
								/>
							}
							label="Preis auf 5 Rappen runden"
						/>
						<Controller
							name="is_req_dr_tax"
							control={control}
							render={({ field }) => (
								<FormControlLabel
									control={
										<Checkbox
											size="small"
											{...field}
											checked={field.value ?? false}
										/>
									}
									label="Ärztesteuerpflichtig"
								/>
							)}
						/>
					</div>
				</div>
				<div className="flex flex-col gap-3">
					{sectionLabel("Material & Rückverfolgung")}
					<div className="grid grid-cols-2 gap-3">
						<Controller
							name="manufacturer"
							control={control}
							render={({ field }) => (
								<TextField
									label="Hersteller"
									size="small"
									{...field}
								/>
							)}
						/>
						<Controller
							name="ce_number"
							control={control}
							render={({ field }) => (
								<TextField
									label="CE-Nummer"
									size="small"
									{...field}
								/>
							)}
						/>
					</div>
					<Controller
						name="lot"
						control={control}
						render={({ field }) => (
							<TextField
								label="LOT-Nummer"
								size="small"
								{...field}
							/>
						)}
					/>
					<Controller
						name="composition"
						control={control}
						render={({ field }) => (
							<TextField
								label="Zusammensetzung"
								size="small"
								multiline
								rows={3}
								{...field}
							/>
						)}
					/>
				</div>
				<div className="flex flex-col gap-3">
					{sectionLabel("Lager & Einkauf")}
					<div className="grid grid-cols-2 gap-3">
						<Controller
							name="stock_quantity"
							control={control}
							render={({ field }) => (
								<TextField
									label="Lager"
									size="small"
									{...field}
								/>
							)}
						/>
						<Controller
							name="purchase_quantity"
							control={control}
							render={({ field }) => (
								<TextField
									label="Einkauf"
									size="small"
									{...field}
								/>
							)}
						/>
					</div>
				</div>
				<div className="flex flex-col gap-3">
					{sectionLabel("Dokumente")}
					<Controller
						name="additional_text"
						control={control}
						render={({ field }) => (
							<TextField
								label="Zusatztext"
								size="small"
								multiline
								rows={3}
								{...field}
							/>
						)}
					/>
				</div>
			</div>
		</form>
	);
};
